import { Rocket, Info, Lock, UserCheck, Link2, Users, Globe, AlertCircle } from 'lucide-react'
import type { App, SourcePreflightResponse } from '../lib/api'
import { StyledSelect } from './ui/StyledSelect'

export interface PublishAppModalProps {
  app: App
  preflight: SourcePreflightResponse | null
  preflightError: string | null
  submitError: string | null
  accessLevel: string
  onAccessLevelChange: (value: string) => void
  onClose: () => void
  onConfirm: () => void
  submitting: boolean
  onViewWorkDetails?: () => void
}

export function PublishAppModal({
  app: _app,
  preflight,
  preflightError,
  submitError,
  accessLevel,
  onAccessLevelChange,
  onClose,
  onConfirm,
  submitting,
  onViewWorkDetails,
}: PublishAppModalProps) {
  const sourceSummary = preflight?.sourceSummary ?? null
  const changedFileCount = sourceSummary?.changedFileCount ?? null
  const savedVersion = sourceSummary?.savedVersionId ?? null
  const hasUnsavedChanges = Boolean(sourceSummary?.hasUnsavedChanges ?? false)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md max-h-[calc(90svh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto p-6">
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
          <Rocket className="w-5 h-5 text-primary" />
          Publish app
        </h2>
        <p className="text-muted-foreground text-sm mb-4">
          This creates a live version of your app.
          Your current work area will keep running alongside it.
        </p>

        {(preflight || preflightError) && (
          <div className="mb-4 rounded-lg border border-border bg-secondary/40 p-3 text-sm">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="font-medium">
                  {preflightError ? 'Safety check unavailable' : 'Publishing check'}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {preflightError || preflight?.summary}
                </p>
                {preflight?.warnings.length ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                    {preflight.warnings.map((warning) => (
                      <li key={`${warning.code}-${warning.message}`}>{warning.message}</li>
                    ))}
                  </ul>
                ) : null}
                {sourceSummary && (
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <dt className="text-muted-foreground">Saved version</dt>
                      <dd className="font-medium">{savedVersion || 'Not saved yet'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Changed files</dt>
                      <dd className="font-medium">{changedFileCount ?? 'Unknown'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Unsaved changes</dt>
                      <dd className="font-medium">{hasUnsavedChanges ? 'Yes' : 'No'}</dd>
                    </div>
                  </dl>
                )}
                {onViewWorkDetails && (
                  <button
                    type="button"
                    onClick={onViewWorkDetails}
                    className="mt-3 text-sm font-medium text-primary hover:underline"
                  >
                    View work details
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="text-sm font-medium mb-1.5 block">Who can access this app?</label>
          <StyledSelect
            value={accessLevel}
            onChange={onAccessLevelChange}
            options={[
              { value: 'personal', label: 'Private', description: 'Only you can access', icon: Lock },
              { value: 'specific', label: 'Specific people', description: 'Only people you add', icon: UserCheck },
              { value: 'link', label: 'Anyone with the link', description: 'Any platform user with the link', icon: Link2 },
              { value: 'platform', label: 'Platform', description: 'Visible to all platform users in Browse Tab', icon: Users },
            ]}
            className="w-full"
          />
        </div>

        {accessLevel === 'platform' && (
          <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
            <Globe className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Your app will be discoverable by all users in the Browse tab.</span>
          </div>
        )}
        {accessLevel === 'link' && (
          <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-600 dark:text-blue-400 flex items-start gap-2">
            <Link2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Visible to any platform user with the link (not visible in Browse tab).</span>
          </div>
        )}
        {accessLevel === 'specific' && (
          <div className="mb-4 p-3 rounded-lg bg-secondary text-xs text-muted-foreground flex items-start gap-2">
            <UserCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              You can add specific users after publishing from the <strong>Sharing</strong> section on this page.
            </span>
          </div>
        )}

        <p className="text-muted-foreground text-xs mb-4">
          This may take a few minutes. You can continue working while it builds.
          You can change visibility settings later from the app details page.
        </p>

        {submitError && (
          <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div className="min-w-0">
              <p className="font-medium text-destructive">Couldn&apos;t start publishing</p>
              <p className="mt-1 text-destructive/90 break-words">{submitError}</p>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Publishing…' : submitError ? 'Try again' : 'Publish app'}
          </button>
        </div>
      </div>
    </div>
  )
}
